import { useMemo } from 'preact/hooks';
import { backButton } from '../../app/backButton';
import { useApp } from '../../app/context';
import type { EngineDevHooks, EngineMount, EngineTestWindow } from '../../game/debug';
import { GameScreen } from '../../game/GameScreen';
import { createGameNetBridge } from '../../net/gameNetBridge';
import { createGameVoiceBridge } from '../../voice/gameVoiceBridge';
import './GameHost.css';

/**
 * Mounts the game engine (<GameScreen/>) for the current race with everything it needs.
 * The engine is remounted when the race or the player's role changes.
 */
export function GameHost() {
  const app = useApp();
  const session = app.race.current.value;
  const myId = app.session.playerId.value;
  const profile = app.session.profile.value;
  const raceId = session?.race.raceId ?? '';

  const net = useMemo(() => createGameNetBridge(app.net, raceId), [app.net, raceId]);
  const voice = useMemo(() => createGameVoiceBridge(app.voice), [app.voice]);
  const me = useMemo(() => (myId && profile ? { id: myId, profile } : null), [myId, profile]);
  // A new session object comes with every new race or role, i.e. with every new game screen.
  const dev = useMemo(
    () =>
      import.meta.env.VITE_E2E === '1' && session && myId
        ? testHooks({
            raceId: session.race.raceId,
            playerId: myId,
            spectator: session.role === 'spectator',
            levels: session.race.levels,
            resumeLevel: session.race.resumeLevel,
            endAt: session.race.endAt,
          })
        : undefined,
    [session, myId],
  );

  if (!session || !me) return null;
  const spectator = session.role === 'spectator';
  return (
    <div class="game-host">
      <GameScreen
        key={`${raceId}:${session.role}`}
        race={session.race}
        me={me}
        spectator={spectator}
        members={app.room.members}
        progress={app.race.progress}
        settings={app.settings.game}
        net={net}
        voice={voice}
        onQuit={() => app.leaveGame()}
        onOpenSettings={() => app.ui.openDialog('settings')}
        registerBack={(handler) => backButton.register(handler)}
        dev={dev}
      />
    </div>
  );
}

/**
 * End-to-end test builds only (VITE_E2E=1): the engine hooks a test installed before the app
 * loaded (e2e/support/bot) — a bot plays through the real input path, the test reads the
 * engine's status. Vite replaces the flag with a literal, so production builds drop this.
 */
function testHooks(mount: EngineMount): EngineDevHooks | undefined {
  const factory = (window as Window & EngineTestWindow).__cubeRushEngineHooks;
  return typeof factory === 'function' ? factory(mount) : undefined;
}
